import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Trophy, Target, Award, Calendar, MessageSquare } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademyPlayerDashboard({ user }) {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/academy/player/dashboard`, {
        withCredentials: true
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const { player, team, stats, enrolled_sessions, recent_feedback } = dashboardData || {};

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-gradient-to-r from-green-600 to-emerald-600 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/academy">
            <Button variant="ghost" className="text-white hover:text-green-200">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Academy
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-white">My Dashboard</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-white mb-2">{player?.child_name}</h2>
          <p className="text-slate-400">{player?.membership_number}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Team Card */}
          {team && (
            <Card className="p-6 bg-slate-900/50 border-2 border-emerald-500/30">
              <h3 className="text-lg font-bold text-white mb-4">My Team</h3>
              <p className="text-2xl font-bold text-emerald-400">{team.name}</p>
              <p className="text-sm text-slate-400 mt-2 capitalize">{team.session_type}</p>
              <Link to={`/academy/teams/${team.id}`}>
                <Button variant="outline" className="w-full mt-4" size="sm">
                  View Team Roster
                </Button>
              </Link>
            </Card>
          )}

          {/* Stats Cards */}
          <Card className="p-6 bg-emerald-500/10 border-2 border-emerald-500/30">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-white">Goals</h3>
              <Trophy className="w-8 h-8 text-emerald-400" />
            </div>
            <p className="text-4xl font-bold text-emerald-400">{stats?.goals_scored || 0}</p>
          </Card>

          <Card className="p-6 bg-blue-500/10 border-2 border-blue-500/30">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-white">Assists</h3>
              <Target className="w-8 h-8 text-blue-400" />
            </div>
            <p className="text-4xl font-bold text-blue-400">{stats?.assists || 0}</p>
          </Card>

          <Card className="p-6 bg-[#0a0f1a] border-2 border-white/5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-white">MOTM Awards</h3>
              <Award className="w-8 h-8 text-yellow-500" />
            </div>
            <p className="text-4xl font-bold text-yellow-500">{stats?.motm_awards || 0}</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* My Sessions (Enrolled) */}
          <Card className="p-6 bg-slate-900/50">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center">
              <Calendar className="w-6 h-6 mr-2 text-emerald-400" />
              My Training Sessions
            </h3>
            {!enrolled_sessions || enrolled_sessions.length === 0 ? (
              <p className="text-slate-400">No sessions enrolled yet</p>
            ) : (
              <div className="space-y-3">
                {enrolled_sessions.map((session) => (
                  <div key={session.id} className="p-3 bg-emerald-500/10 rounded-lg border border-green-500/30">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-white">{session.name || session.session_type}</p>
                        <p className="text-sm text-slate-400">
                          {session.day_of_week} {session.start_time && `at ${session.start_time}`}
                        </p>
                        {session.location_name && (
                          <p className="text-sm text-slate-400">{session.location_name}</p>
                        )}
                      </div>
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-green-200 text-emerald-400">
                        Enrolled
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Recent Feedback */}
          <Card className="p-6 bg-slate-900/50">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center">
              <MessageSquare className="w-6 h-6 mr-2 text-purple-400" />
              Recent Feedback
            </h3>
            {!recent_feedback || recent_feedback.length === 0 ? (
              <p className="text-slate-400">No feedback yet</p>
            ) : (
              <div className="space-y-3">
                {recent_feedback.map((feedback) => (
                  <div key={feedback.id} className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                    <div className="flex justify-between items-start mb-2">
                      <p className="text-sm font-semibold text-white">{feedback.coach_name}</p>
                      <p className="text-xs text-slate-400">
                        {new Date(feedback.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <p className="text-sm text-slate-300">{feedback.feedback_text}</p>
                  </div>
                ))}
                <Link to="/academy/feedback">
                  <Button variant="outline" className="w-full mt-2" size="sm">
                    View All Feedback
                  </Button>
                </Link>
              </div>
            )}
          </Card>
        </div>

        {/* Browse All Teams CTA */}
        <Card className="p-8 mt-6 bg-gradient-to-r from-slate-900 to-slate-900 border-2 border-yellow-500/30 text-center">
          <h3 className="text-2xl font-bold text-white mb-2">🏆 Explore Academy</h3>
          <p className="text-slate-300 mb-4">Check out other teams and their recent game results</p>
          <Link to="/academy/browse-teams">
            <Button className="bg-yellow-600 hover:bg-yellow-700">
              Browse All Teams
            </Button>
          </Link>
        </Card>
      </div>
    </div>
  );
}
